import { useState } from 'react';

import * as Endpoint from "../../../endpoints";

import IObservation from '../../interfaces/IObservation';
import { makeFailure, makeSuccess, TResult, getSgApiService, useLoading } from '../../../../sg-core';
import { useDispatch } from 'react-redux';
import { useAuthentication } from '../../auth/store/authSelectors';
import { getObservationService } from '../../services/observationService';
import { useMakeObservationPackage } from './useMakeObservationPackage';
import { useSaveHistoryPackage } from './useSaveHistoryPackage';
import { createRequestFetchObservationAction } from '../../store/actions/observationActions';
import IFormType from '../../interfaces/IFormType';
import { TCreateObservationEvent } from '../../interfaces/IObservationEvent';
import { observationToSearchableText } from '../../utils/observationToSearchableText';
import { observationToPersonIds } from '../../utils/data.helpers';
import { getPersonService } from '../../services/personService';
import IPerson from '../../interfaces/IPerson';


interface IUseSaveObservationData {
    save: (formData: IObservation["formData"]) => Promise<TResult<IObservation>>;
    saving: boolean;
    saveError?: string;
    savedAt?: Date;
}

export type IUseSaveObservationHook = (observation: IObservation | null, formType: IFormType["id"]) => IUseSaveObservationData;


export const useSaveObservation: IUseSaveObservationHook = (originalObservation, formType) => {
    const {
        loading: saving,
        loadError: saveError,
        startLoading,
        loadSuccess,
        loadErrored,
    } = useLoading();

    const { token, anonymousSession } = useAuthentication();

    const dispatch = useDispatch();
    const { makePackage } = useMakeObservationPackage();
    const { saveHistoryPackage } = useSaveHistoryPackage();


    const [savedAt, setSavedAt] = useState<Date>();

    const apiService = getSgApiService("");
    apiService.setToken(token || "");
    const observationService = getObservationService(apiService, Endpoint.havainnot, anonymousSession);
    const personService = getPersonService(apiService, Endpoint.HPR, anonymousSession)

    const savePacakge = async (eventId: number, data: IObservation, event?: TCreateObservationEvent): Promise<TResult<IObservation>> => {
        try {
            const dataPackage = await makePackage({ observation: data });
            if (!dataPackage.isSuccess) { throw dataPackage.error; }

            const historyPackage = await saveHistoryPackage(dataPackage.value);
            if (!historyPackage.isSuccess) { throw historyPackage.error };

            const eventsResult = await observationService.updateHistoryEvent(eventId, { ...(event || {}), historyPackageId: historyPackage.value });
            if (!eventsResult.isSuccess) { throw eventsResult.error; };

            return makeSuccess(data);

        } catch (error) {
            return makeFailure(error as Error);
        }
    }

    const save = async (formData: IObservation["formData"]): Promise<TResult<IObservation>> => {
        // if (saving) { return makeFailure(Error("save permit prevented")) };
        try {

            startLoading();
            if (!originalObservation || !originalObservation.formSchema) { return makeFailure(Error("Missing original observation")) };
            if (!formData) { return makeFailure(Error("Missing form data")) };





            const updatedObservation: IObservation = {
                ...originalObservation,
                formType,
                formData: formData,
            }



            const personIds = observationToPersonIds(updatedObservation.formData, updatedObservation.formSchema);
            const personResult: TResult<IPerson[]> = await personService.getPersons(personIds);
            if (!personResult.isSuccess) { throw personResult.error };

            updatedObservation.searchText = observationToSearchableText(formData, originalObservation.formSchema, personResult.value, []);



            const historyEvents = await observationService.getHistoryEvents(originalObservation.id);
            if (!historyEvents.isSuccess) { throw historyEvents.error };
            const initialEvent = historyEvents.value.find((event) => event.type === "havainnon_luonti");


            // Create initial event if non was found
            if (!historyEvents.value.length) {
                const saveResult = await observationService.saveObservation(originalObservation);
                if (!saveResult.isSuccess) { throw saveResult.error };
                const packageResult = await savePacakge(saveResult.value.eventId, saveResult.value.observation, { title: "Havainto luotu", type: "havainnon_luonti" });
                if (!packageResult.isSuccess) { throw packageResult.error };

                // Add package of original observation if missing
            } else if (initialEvent && !initialEvent.historyPackageId) {
                const packageResult = await savePacakge(initialEvent.id, originalObservation, { title: "Havainto luotu", type: "havainnon_luonti" });
                if (!packageResult.isSuccess) { throw packageResult.error };
            };

            const saveResult = await observationService.saveObservation(updatedObservation);
            if (!saveResult.isSuccess) { throw saveResult.error };

            const packageResult = await savePacakge(saveResult.value.eventId, saveResult.value.observation);
            if (!packageResult.isSuccess) { throw packageResult.error };

            setSavedAt(new Date());
            loadSuccess();

            dispatch(createRequestFetchObservationAction(saveResult.value.observation.guid));
            // dispatch(createSuccessFetchObservationAction(saveResult.value.observation));

            return makeSuccess(saveResult.value.observation);
        } catch (error) {
            console.info(error)
            loadErrored((error as Error).message);
            return makeFailure(error as Error);
        };
    };

    return {
        save,
        saving,
        saveError,
        savedAt
    };
};


